import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { reportKeys } from "@/lib/query-keys";
import type { PaginatedResponse, ProjectReport } from "@/types/api";
import type { PreconTemplateMetadata } from "@/components/reports/precon-report-builder/precon-types";

export type ReportTemplate =
  | "STANDARD"
  | "ACTION_REPORT"
  | "PHOTOS_ONLY"
  | "PRECON_SURVEY";
export type ReportOrientation = "PORTRAIT" | "LANDSCAPE";
export type ReportFormat = "PDF" | "PPTX";

/** A per-photo caption entry (PRECON_SURVEY only) — captions key on image_id. */
export interface GenerateReportPhoto {
  image_id: string;
  caption: string;
}

export interface GenerateReportRecord {
  id: string;
  /** Per-record remark override — used by the non-precon report templates. */
  remark?: string;
  /**
   * Per-photo captions (PRECON_SURVEY only): every photo of the record, in
   * `taken_at` order. The precon flow sends this instead of `remark`; the
   * backend renders all photos sub-lettered (see [[precon-report-builder.spec]]).
   */
  photos?: GenerateReportPhoto[];
}

export interface GenerateReportPayload {
  records: GenerateReportRecord[];
  name?: string;
  template: ReportTemplate;
  /** Omitted when format === "PPTX" (PPTX is always landscape; backend ignores orientation). */
  orientation?: ReportOrientation;
  /** Output format. Defaults to "PDF". */
  format: ReportFormat;
  /**
   * Write edited remarks back to the source records. Optional: the precon flow
   * omits it (captions there are report-only); the other templates send it.
   */
  override_remarks?: boolean;
  template_metadata?: PreconTemplateMetadata; // present only for PRECON_SURVEY
}

type ReportListCache = InfiniteData<PaginatedResponse<ProjectReport>>;

/**
 * Mirror of otus-web's `generateProjectReportV3Action` flow:
 * - POST /reports/2.2/projects/{projectId}/reports/generate/
 * - Backend responds 202 Accepted with the freshly-created PENDING report
 * - On success: prepend optimistically to the list cache, seed the detail
 *   cache so the polling row finds initial data immediately, then
 *   invalidate the list to trigger a background refresh.
 *
 * The dialog is responsible for the loading / error UI states; this hook
 * surfaces the mutation as-is and lets `useMutation` errors bubble.
 */
export function useGenerateReport(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      records,
      name,
      template,
      orientation,
      format,
      override_remarks,
      template_metadata,
    }: GenerateReportPayload) => {
      // Backend expects lowercase for format ("pdf" | "pptx") but UPPERCASE
      // for orientation ("PORTRAIT" | "LANDSCAPE") — DRF ChoiceField over
      // TextChoices with no normalization. `template` is also sent as-is
      // (already uppercase). Orientation is omitted for PPTX (backend forces
      // landscape; must not error on missing field).
      const body = {
        records,
        name,
        template,
        format: format.toLowerCase(),
        ...(format === "PDF" ? { orientation } : {}),
        override_remarks,
        template_metadata,
      };
      return fetchWithAuth<ProjectReport>(
        `/reports/2.2/projects/${projectId}/reports/generate/`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: (report) => {
      // Optimistically prepend the new (PENDING) report to the list cache
      // so the user lands on /reports with their report already at the top.
      queryClient.setQueryData<ReportListCache>(
        reportKeys.list(projectId),
        (oldData) => {
          if (!oldData || oldData.pages.length === 0) {
            return {
              pages: [
                {
                  count: 1,
                  next: null,
                  previous: null,
                  results: [report],
                },
              ],
              pageParams: [1],
            };
          }
          const [firstPage, ...rest] = oldData.pages;
          return {
            ...oldData,
            pages: [
              {
                ...firstPage,
                count: firstPage.count + 1,
                results: [report, ...firstPage.results],
              },
              ...rest,
            ],
          };
        },
      );

      // Seed the detail-cache so the polling hook in reports-view sees
      // initial data on first render rather than triggering its own GET.
      queryClient.setQueryData(
        reportKeys.detail(projectId, report.id),
        report,
      );

      // Invalidate the list so a subsequent visit reloads from the server
      // cleanly.
      void queryClient.invalidateQueries({
        queryKey: reportKeys.list(projectId),
      });
    },
  });
}
